/** @jsxImportSource preact */
import type { ComponentChildren } from "preact";
import { useLayoutEffect, useRef } from "preact/hooks";

import type { ProactivePrompt } from "../proactive/proactive";
import { CloseIcon } from "./icons";
import { SR_ONLY } from "./sr-only";
import { withAlpha, type WidgetTheme } from "./theme";
import { useLauncherDrag, type LauncherDrag } from "./use-launcher-drag";
import {
  WidgetCharacter,
  type WidgetCharacterMotion,
  type WidgetCharacterVariant,
} from "./widget-character";

/** 入口上方那块提示实际画出来要的东西，主动提示和回复提示都换算成它。 */
export interface LauncherPrompt {
  text: ComponentChildren;
  /** 按钮名称，出现时也在入口的 live region 里播报：收起后聊天窗对读屏是隐藏的。 */
  label?: string;
  activate?: () => void;
  dismiss?: { label: string; onClick: () => void };
  /** 拉长身体时角色的动作；独立气泡旁的入口角色按会话状态自己演。 */
  motion: WidgetCharacterMotion;
}

/** 提示气泡和加载卡片共用的壳：毛玻璃或实底描边，朝角色那一侧的下角收小。 */
export function launcherBubbleStyle(
  theme: WidgetTheme,
  glassBubble: boolean,
  alignRight: boolean,
) {
  return {
    display: "flex",
    alignItems: "flex-start",
    maxWidth: "100%",
    boxSizing: "border-box",
    color: theme.ink,
    ...(glassBubble
      ? {
          // 半透明底加模糊，不描边；朝角色那个下角收小。
          background: withAlpha(
            theme.dark ? theme.surfaceSolid : "#FFFFFF",
            0.72,
          ),
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          borderRadius: alignRight
            ? `${theme.bubbleRadius} ${theme.bubbleRadius} calc(${theme.bubbleRadius} / 4) ${theme.bubbleRadius}`
            : `${theme.bubbleRadius} ${theme.bubbleRadius} ${theme.bubbleRadius} calc(${theme.bubbleRadius} / 4)`,
          boxShadow: "0 8px 24px rgba(28,27,26,.08)",
        }
      : {
          border: `1px solid ${theme.line}`,
          borderRadius: theme.panelRadius,
          background: theme.cardBg,
          backdropFilter: theme.panelBackdrop,
          WebkitBackdropFilter: theme.panelBackdrop,
          boxShadow: "0 6px 20px rgba(0,0,0,.08)",
        }),
  } as const;
}

/**
 * 主动提示在入口阶段和完整聊天窗里的同一换算：不设 label，
 * 不进 live region 播报（播报是回复提示的待遇），按钮名取提示文本本身。
 */
export function proactiveLauncherPrompt(
  prompt: ProactivePrompt,
  dismissLabel: string,
): LauncherPrompt {
  return {
    text: prompt.text,
    activate: prompt.activate,
    dismiss: { label: dismissLabel, onClick: prompt.dismiss },
    motion: "still",
  };
}

/** 44px 点击区的透明图标按钮：提示关闭键和聊天窗标题栏共用。 */
export const headerButtonStyle = {
  background: "transparent",
  border: 0,
  borderRadius: 9,
  color: "inherit",
  cursor: "pointer",
  display: "grid",
  height: 44,
  padding: 0,
  placeItems: "center",
  width: 44,
} as const;

export interface LauncherCapsuleProps {
  /** 入口停靠的墙；左边时整只镜像、气泡尾巴换到左下。 */
  position: "left" | "right";
  /** data-capsule 的现形；聊天窗开着时入口收起。 */
  size: "small" | "medium" | "large";
  /** 聊天窗开着：入口角色缩小淡出，点按变成收起。 */
  open: boolean;
  /** 面板还在退出动画里：入口高度归零、toggle 绝对定位让位。 */
  present: boolean;
  /** 主动提示或回复提示换算好的展示模型。 */
  prompt?: LauncherPrompt;
  /** 拉长身体的提示形态：提示贴在角色身上，不再单独摆 toggle。 */
  bodyPrompt?: boolean;
  /** 给了才能把入口拖到左右两边，吸附和记住位置由挂载根负责。 */
  drag?: LauncherDrag;
  launcherLabel: string;
  onOpen?: () => void;
  onClose?: () => void;
  /** 入口角色画法（实色、水彩玻璃等），按商家配置换算好传入。 */
  character: WidgetCharacterVariant;
  /** 入口角色当前动作。 */
  motion: WidgetCharacterMotion;
  theme: WidgetTheme;
  /** 入口角色圆角。 */
  characterRadius: string;
  /** 拉长身体时提示正文的圆角变量。 */
  promptRadius: string;
  /** 毛玻璃或水彩角色时，提示气泡用半透明材质。 */
  glassBubble: boolean;
  /** 提示和 toggle 之间的额外内容（入口阶段的加载卡片）。 */
  children?: ComponentChildren;
}

/**
 * 收起入口：角色 toggle、主动提示气泡、回复提示都长在这里。
 * 店面入口阶段和完整聊天窗共用这一份实现，避免两套入口漂移。
 */
export function LauncherCapsule({
  position,
  size,
  open,
  present,
  prompt,
  bodyPrompt = false,
  drag: launcherDrag,
  launcherLabel,
  onOpen,
  onClose,
  character,
  motion,
  theme,
  characterRadius,
  promptRadius,
  glassBubble,
  children,
}: LauncherCapsuleProps) {
  const drag = useLauncherDrag(!open && !bodyPrompt ? launcherDrag : undefined);
  const alignRight = position !== "left";
  const entryRef = useRef<HTMLButtonElement>(null);
  const dismissedPrompt = useRef(false);
  useLayoutEffect(() => {
    if (dismissedPrompt.current && !prompt) {
      entryRef.current?.focus();
      dismissedPrompt.current = false;
    }
  }, [prompt]);
  const dismissPrompt = () => {
    dismissedPrompt.current = true;
    prompt?.dismiss?.onClick();
  };
  // 可关闭的聊天窗只保留标题栏关闭键；开着时点入口收起来。
  const launcherClosesPanel = open && onClose !== undefined;
  return (
    <>
      {/* 落点虚框在入口之前，角色拖到虚框上时盖住它；固定定位，放在带位移的入口外面。 */}
      {launcherDrag && (
        <div
          ref={drag.ghost}
          class="gn-launcher-ghost"
          style={{ borderRadius: characterRadius }}
        />
      )}
      <div
        ref={drag.launcher}
        class="gn-launcher"
        data-capsule={size}
        data-position={position}
        style={{
          position: "relative",
          height: present && onClose ? 0 : undefined,
          display: "flex",
          flexDirection: "column",
          alignItems: alignRight ? "flex-end" : "flex-start",
          gap: 12,
          maxWidth: "100%",
        }}
      >
        {/* live region 要常驻、到时候才填字，跟着提示整块条件渲染读屏播不出来。 */}
        <span role="status" style={SR_ONLY}>
          {prompt?.label ?? ""}
        </span>
        {prompt && !bodyPrompt && (
          <div
            class="gn-proactive-bubble"
            style={launcherBubbleStyle(theme, glassBubble, alignRight)}
          >
            <button
              class="gn-proactive-copy"
              type="button"
              aria-label={prompt.label}
              onClick={prompt.activate}
              style={{
                border: 0,
                borderRadius: "inherit",
                background: "transparent",
                color: "inherit",
                cursor: "pointer",
                font: "inherit",
                lineHeight: 1.45,
                padding: prompt.dismiss ? "13px 8px 13px 15px" : "13px 15px",
                minWidth: 0,
                textAlign: "start",
                overflowWrap: "anywhere",
                whiteSpace: "pre-wrap",
              }}
            >
              {prompt.text}
            </button>
            {prompt.dismiss && (
              <button
                aria-label={prompt.dismiss.label}
                onClick={dismissPrompt}
                style={{ ...headerButtonStyle, flex: "none", margin: 5 }}
                type="button"
              >
                <CloseIcon size={15} />
              </button>
            )}
          </div>
        )}
        {bodyPrompt && prompt && (
          <div
            style={{
              position: "relative",
              width: 320,
              maxWidth: "100%",
              "--gn-character-prompt-radius": promptRadius,
            }}
          >
            <WidgetCharacter
              color={theme.accent}
              eyeColor={theme.accentFg}
              motion={prompt.motion}
              prompt={prompt}
              variant={character}
            />
            {prompt.dismiss && (
              <button
                aria-label={prompt.dismiss.label}
                onClick={dismissPrompt}
                style={{
                  ...headerButtonStyle,
                  position: "absolute",
                  right: 5,
                  top: 5,
                  // 水彩身体的正文区是浅底深字，关闭键跟正文同色。
                  color:
                    character === "watercolor"
                      ? `color-mix(in srgb, ${theme.accent}, #000 65%)`
                      : theme.accentFg,
                }}
                type="button"
              >
                <CloseIcon size={15} />
              </button>
            )}
          </div>
        )}
        {children}
        {!bodyPrompt && (
          <button
            ref={entryRef}
            class="gn-launcher-toggle"
            aria-hidden={launcherClosesPanel || undefined}
            tabIndex={launcherClosesPanel ? -1 : undefined}
            aria-expanded={open}
            aria-label={launcherLabel}
            {...drag.handlers}
            onClick={() => {
              if (drag.dragged.current) return;
              (launcherClosesPanel
                ? onClose
                : (prompt?.activate ?? onOpen))?.();
            }}
            style={{
              background: "transparent",
              color: theme.accentFg,
              border: 0,
              borderRadius: characterRadius,
              cursor: "pointer",
              display: "grid",
              flex: "none",
              height: 56,
              width: 56,
              padding: 0,
              placeItems: "center",
              position: present && onClose ? "absolute" : "relative",
              bottom: 0,
              touchAction: launcherDrag ? "none" : undefined,
            }}
            type="button"
          >
            <WidgetCharacter
              color={theme.accent}
              eyeColor={theme.accentFg}
              radius={characterRadius}
              motion={motion}
              variant={character}
            />
          </button>
        )}
      </div>
    </>
  );
}
